import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import * as turf from '@turf/turf';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import { getObstacleIcon, getShieldComboIcon } from '../../utils/markers';
import { assessRnavRoutes, assessRnavCandidates, saveRnavScreenshot, getRnavScreenshot } from '../../api/client';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

export default function RnavRoutesPage({ project, obstacles, loading: projectLoading, state, setState, showObstacles = true, showObsLabels, onObstacleContextMenu, onMapReady }) {
    const { getStyle } = useSurfaceStyles();
    const routeActiveStyle = getStyle('rnav.route_active');
    const routeInactiveStyle = getStyle('rnav.route_inactive');
    const bufferPrimaryStyle = getStyle('rnav.buffer_primary');
    const bufferSecondaryStyle = getStyle('rnav.buffer_secondary');
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    const tileRef = useRef(null);
    const layersRef = useRef([]);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [exportingAixm, setExportingAixm] = useState(false);
    const [assessResults, setAssessResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeRouteId, setActiveRouteId] = useState(null);
    // Step 2 reference set — this project's own obstacles that actually
    // PENETRATE a route's real published MOCA on the segment they're
    // nearest to (not just any obstacle sitting inside the 5.77 NM buffer —
    // most obstacles in the buffer sit comfortably below MOCA and aren't a
    // violation of anything).
    const penetratingObstacleIds = useMemo(() => (
        assessResults.flatMap(r => (r.obstacles_in_buffer || []).filter(o => o.penetrates).map(o => o.id))
    ), [assessResults]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv: exportShieldCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);

    // ── RNAV route shielding — real per-segment MOCA check ──────────────────
    // Published MOCA is flat per segment, but different segments on
    // different (or even the same) routes carry different values, so a
    // candidate nearest a stricter segment at a modest elevation can
    // genuinely penetrate while the project's own (taller) obstacle sits
    // nearest a segment with a more generous MOCA — same gap flat elevation
    // comparison had for IHS (see OLSPage.jsx). margin_ft = published MOCA -
    // total_elev_ft, negative = penetrates (matches OLS's convention).
    const rnavImpacts = useMemo(() => {
        const out = {}; // `${route_name}_${segment_from}-${segment_to}` -> worst (most negative) margin_ft
        assessResults.forEach(r => {
            (r.obstacles_in_buffer || []).forEach(o => {
                if (!o.penetrates || o.margin_ft == null) return;
                const key = `${r.route_name}_${o.segment_from}-${o.segment_to}`;
                if (!(key in out) || o.margin_ft < out[key]) out[key] = o.margin_ft;
            });
        });
        return out;
    }, [assessResults]);
    const hasRnavImpact = Object.keys(rnavImpacts).length > 0;

    const [rnavShieldPens, setRnavShieldPens] = useState([]); // candidate result rows (flat, one per obstacle+route)
    useEffect(() => {
        if (!shieldOn || !shieldData || !hasRnavImpact) { setRnavShieldPens([]); return; }
        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setRnavShieldPens([]); return; }
        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));
        let cancelled = false;
        (async () => {
            try {
                const { data } = await assessRnavCandidates(pseudoObstacles);
                if (!cancelled) setRnavShieldPens(data.results || []);
            } catch (e) {
                console.error('RNAV shield check failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasRnavImpact, rnavImpacts]);

    // Candidate pseudo-id -> [{ route_name, segment_from, segment_to, margin, projectMargin }, ...]
    // — beats the project's own worst on that route+segment (the amber
    // "true shield" check).
    const rnavShieldVerdicts = useMemo(() => {
        const out = new Map();
        rnavShieldPens.forEach(r => {
            if (!r.penetrates || r.margin_ft == null) return;
            const key = `${r.route_name}_${r.segment_from}-${r.segment_to}`;
            const projectMargin = rnavImpacts[key];
            if (projectMargin == null) return;
            if (r.margin_ft <= projectMargin) {
                const list = out.get(r.obstacle_id) || [];
                list.push({ route_name: r.route_name, segment_from: r.segment_from, segment_to: r.segment_to, margin: r.margin_ft, projectMargin });
                out.set(r.obstacle_id, list);
            }
        });
        return out;
    }, [rnavShieldPens, rnavImpacts]);

    const rnavShieldInsideSurface = useMemo(() => {
        const out = new Map();
        rnavShieldPens.forEach(r => { if (r.has_published_moca) out.set(r.obstacle_id, true); });
        return out;
    }, [rnavShieldPens]);

    // Candidate pseudo-id -> [{ route_name, segment_from, segment_to, margin }, ...]
    // for every route segment it genuinely penetrates at its own position,
    // regardless of raw elevation or of beating the project's worst — see
    // olsShieldRealPenetration in OLSPage.jsx.
    const rnavShieldRealPenetration = useMemo(() => {
        const out = new Map();
        rnavShieldPens.forEach(r => {
            if (!r.penetrates || r.margin_ft == null) return;
            const list = out.get(r.obstacle_id) || [];
            list.push({ route_name: r.route_name, segment_from: r.segment_from, segment_to: r.segment_to, margin: r.margin_ft });
            out.set(r.obstacle_id, list);
        });
        return out;
    }, [rnavShieldPens]);

    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real segment's MOCA
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasRnavImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = rnavShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = rnavShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = rnavShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v => `${v.route_name} ${v.segment_from}-${v.segment_to} (${(-v.margin).toFixed(0)} ft)`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">RNAV ${v.route_name} ${v.segment_from}-${v.segment_to}</b> — penetrates published MOCA by ${(-v.margin).toFixed(0)} ft (project worst: ${(-v.projectMargin).toFixed(0)} ft)`
                  ).join('')
                : `<br/><span style="color:#8a94a6">RNAV MOCA: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, rnavShieldVerdicts, rnavShieldInsideSurface, rnavShieldRealPenetration, hasRnavImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield a
    // route segment's published MOCA — same pattern as OLSPage.jsx.
    const rnavTrueShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (rnavTrueShieldLayerRef.current) { mapInst.current.removeLayer(rnavTrueShieldLayerRef.current); rnavTrueShieldLayerRef.current = null; }
        if (!shieldOn || rnavShieldVerdicts.size === 0) return;
        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });
        rnavShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const lines = verdicts.map(v =>
                `<b>${v.route_name} ${v.segment_from}-${v.segment_to}</b> — penetrates MOCA by <b>${(-v.margin).toFixed(0)} ft</b> (project worst: ${(-v.projectMargin).toFixed(0)} ft)`
            ).join('<br/>');
            marker.bindPopup(
                `<b>🛰 TRUE SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact ${verdicts.length > 1 ? 'on these segments' : 'on this segment'}.</span>`
            );
            group.addLayer(marker);
        });
        if (group.getLayers().length > 0) {
            rnavTrueShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, rnavShieldVerdicts, shieldData]);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(300, { min: 120, max: 700 });
    const [capturing, setCapturing] = useState(false);
    const [hasScreenshot, setHasScreenshot] = useState(false);
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    // Load RNAV routes assessment results
    const loadAssessment = useCallback(async () => {
        if (!project?.id) return;
        setLoading(true);
        setError('');
        try {
            const { data } = await assessRnavRoutes(project.id);
            setAssessResults(data || []);
            if (data && data.length > 0) {
                setActiveRouteId(data[0].route_id);
            }
        } catch (err) {
            console.error('Failed to load RNAV Routes assessment', err);
            setError(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    }, [project?.id]);

    useEffect(() => {
        loadAssessment();
    }, [loadAssessment]);

    // Check if map screenshot exists
    useEffect(() => {
        if (!project?.id) return;
        getRnavScreenshot('rnav_routes', project.id)
            .then(() => setHasScreenshot(true))
            .catch(() => setHasScreenshot(false));
    }, [project?.id]);

    // Sync results back to parent state if needed
    useEffect(() => {
        if (assessResults && setState) {
            setState(prev => {
                if (JSON.stringify(prev.rnavResults) === JSON.stringify(assessResults)) return prev;
                return { ...prev, rnavResults: assessResults };
            });
        }
    }, [assessResults, setState]);

    // Initialize Leaflet Map
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        
        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);

        return () => {
            ro.disconnect();
            mapInst.current?.remove();
            mapInst.current = null;
            tileRef.current = null;
            setMapState(null);
            onMapReady?.(null);
        };
    }, []);

    // Handle Map Mode Toggle
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    const activeRoute = assessResults.find(r => r.route_id === activeRouteId);

    // Draw Map Layers (Routes, Buffers, Obstacles)
    useEffect(() => {
        if (!mapInst.current || loading || projectLoading) return;

        // Clear previous layers
        layersRef.current.forEach(layer => mapInst.current.removeLayer(layer));
        layersRef.current = [];

        const bounds = [];
        const activeRouteGeom = activeRoute?.coordinates;

        // 1. Draw all routes
        assessResults.forEach(r => {
            const isActive = r.route_id === activeRouteId;
            const latlngs = r.coordinates;
            if (!latlngs || latlngs.length < 2) return;

            // Polyline
            const color = isActive ? routeActiveStyle.color : routeInactiveStyle.color;
            const weight = isActive ? 4 : 2;
            const mocaTooltip = r.controlling_obstacle_moca_ft != null
                ? `Controlling obstacle: ${r.controlling_obstacle_name} — obstacle MOCA ${r.controlling_obstacle_moca_ft.toFixed(0)} ft` +
                  (r.controlling_segment_moca_ft != null ? ` (published MOCA ${r.controlling_segment_moca_ft.toFixed(0)} ft)` : ' (no published MOCA nearby)')
                : 'No obstacles in the buffer on this route';
            const poly = L.polyline(latlngs, { color, weight, opacity: 0.9 })
                .bindTooltip(`<b>Route: ${r.route_name}</b><br/>${mocaTooltip}`, { sticky: true });

            poly.addTo(mapInst.current);
            layersRef.current.push(poly);

            if (isActive) {
                latlngs.forEach(pt => bounds.push(pt));
                // Waypoint markers — fly-by symbol at every named significant
                // point along the route (RNAV waypoints are always fly-by,
                // never fly-over, unlike some STAR/SID procedure turns).
                (r.waypoints || []).forEach(wp => {
                    const wm = L.marker([wp.lat, wp.lon], {
                        icon: L.icon({ iconUrl: '/symbols/flyby.svg', iconSize: [18, 18], iconAnchor: [9, 9] }),
                    });
                    wm.addTo(mapInst.current);
                    layersRef.current.push(wm);
                    // Waypoint name — always on screen (not just on hover),
                    // same lightweight plain-text label style used for STAR
                    // procedure waypoints (ArrivalOisPage.jsx's waypointLabel).
                    const label = L.marker([wp.lat, wp.lon], {
                        icon: L.divIcon({
                            className: '',
                            html: `<div style="white-space:nowrap; padding-left:11px; font-size:10px; font-weight:700; color:#fff; text-shadow:1px 1px 2px #000,-1px -1px 2px #000,1px -1px 2px #000,-1px 1px 2px #000;">${wp.name}</div>`,
                            iconSize: [140, 14],
                            iconAnchor: [0, 7],
                        }),
                        interactive: false,
                    });
                    label.addTo(mapInst.current);
                    layersRef.current.push(label);
                });
            }
        });

        // 2. Draw active route's primary (2.885 NM, full MOC) and secondary
        // (2.885 NM further out, MOC reducing to 0) areas. Secondary is
        // drawn first (larger, outer ring) so primary layers on top of it.
        if (activeRoute && activeRouteGeom && activeRouteGeom.length >= 2) {
            try {
                // Turf expects Coordinates as [lng, lat]
                const turfPoints = activeRouteGeom.map(p => [p[1], p[0]]);
                const line = turf.lineString(turfPoints);
                const secondaryBuffered = turf.buffer(line, 5.77, { units: 'nauticalmiles' });
                const primaryBuffered = turf.buffer(line, 2.885, { units: 'nauticalmiles' });

                if (secondaryBuffered) {
                    const secondaryLayer = L.geoJSON(secondaryBuffered, {
                        style: {
                            color: bufferSecondaryStyle.color,
                            weight: 1.5,
                            fillColor: bufferSecondaryStyle.color,
                            fillOpacity: bufferSecondaryStyle.opacity,
                            dashArray: '5,5'
                        }
                    }).addTo(mapInst.current);
                    layersRef.current.push(secondaryLayer);

                    const coords = secondaryBuffered.geometry.type === 'Polygon'
                        ? secondaryBuffered.geometry.coordinates[0]
                        : secondaryBuffered.geometry.coordinates.flatMap(p => p[0]);
                    coords.forEach(pt => bounds.push([pt[1], pt[0]]));
                }

                if (primaryBuffered) {
                    const primaryLayer = L.geoJSON(primaryBuffered, {
                        style: {
                            color: bufferPrimaryStyle.color,
                            weight: 1.5,
                            fillColor: bufferPrimaryStyle.color,
                            fillOpacity: bufferPrimaryStyle.opacity,
                            dashArray: '5,5'
                        }
                    }).addTo(mapInst.current);
                    layersRef.current.push(primaryLayer);
                }
            } catch (e) {
                console.error('Turf buffer generation failed', e);
            }
        }

        // 3. Draw project obstacles
        getObstaclesToRender(obstacles, project?.id, showObstacles).forEach(o => {
            if (!o.latitude || !o.longitude) return;
            const ll = [o.latitude, o.longitude];

            // Check if this obstacle falls within the active route's buffer,
            // and if so, pull its REAL per-segment published-MOCA verdict.
            const bufEntry = activeRoute?.obstacles_in_buffer.find(ob => ob.id === o.id);
            const isInsideActiveBuffer = !!bufEntry;

            // Icon status: penetrated (red) only if it actually violates the
            // published MOCA — merely sitting inside the buffer isn't itself
            // a violation (green), unassessed obstacles stay default (grey).
            const status = activeRoute ? (bufEntry?.penetrates ? 'penetrated' : (isInsideActiveBuffer ? 'clear' : undefined)) : undefined;
            const icon = getObstacleIcon(L, o, status, showObsLabels);

            let penetrationStr = '—';
            let surfaceStr = '—';
            let areaStr = '—';
            if (activeRoute && bufEntry) {
                surfaceStr = `Segment ${bufEntry.segment_from || '?'}-${bufEntry.segment_to || '?'} (${activeRoute.route_name})`;
                areaStr = `${bufEntry.area === 'primary' ? 'Primary' : 'Secondary'} area — MOC applied ${bufEntry.moc_applied_ft?.toFixed(0)} ft`;
                if (!bufEntry.has_published_moca) {
                    penetrationStr = 'No published MOCA for this segment';
                } else if (bufEntry.penetrates) {
                    penetrationStr = `${Math.abs(bufEntry.margin_ft).toFixed(0)} ft ABOVE published MOCA (${bufEntry.segment_moca_ft} ft)`;
                } else {
                    penetrationStr = `${bufEntry.margin_ft.toFixed(0)} ft below published MOCA (${bufEntry.segment_moca_ft} ft) — Clear`;
                }
            } else if (activeRoute) {
                surfaceStr = `Route Buffer (${activeRoute.route_name})`;
                penetrationStr = 'Outside buffer';
            }

            const m = L.marker(ll, { icon })
                .bindPopup(`<b>${o.name || 'Unnamed Obstacle'}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>AREA: ${areaStr}<br/>PENETRATION: ${penetrationStr}`);
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            layersRef.current.push(m);
            
            if (isInsideActiveBuffer) {
                bounds.push(ll);
            }
        });
        addHullPolygonsToMap(mapInst.current, obstacles, project?.id, layersRef);
        

        // Fit map bounds
        if (bounds.length > 0 && mapInst.current) {
            mapInst.current.fitBounds(L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 12 });
        }
    }, [assessResults, activeRouteId, obstacles, loading, projectLoading, showObstacles, showObsLabels, onObstacleContextMenu, routeActiveStyle, routeInactiveStyle, bufferPrimaryStyle, bufferSecondaryStyle]);

    // Handle Map Viewport Capture (Map Screenshot)
    const handleCapture = async () => {
        if (!mapInst.current || !project?.id) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            // 1. Draw map tiles
            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch {}
            }));

            // 2. Draw overlay (SVG layers)
            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            // 3. Draw markers
            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            await saveRnavScreenshot(project.id, 'rnav_routes', dataUrl);
            setHasScreenshot(true);
            alert(`✓ RNAV Routes Map captured successfully!`);
        } catch (err) {
            console.error('Capture failed:', err);
            alert(`✕ Failed to capture map screenshot: ${err.message}`);
        } finally {
            setCapturing(false);
        }
    };

    const exportCsv = () => {
        if (!activeRoute) return;
        const headers = ["Obstacle Name", "Distance (NM)", "Distance (km)", "Elevation (m)", "V-Accuracy (m)", "Total Elev (ft)", "Area", "MOC Applied (ft)", "Obstacle MOCA (ft)", "Segment", "Published MOCA (ft)", "MEA (FL)", "Status"];
        const rows = activeRoute.obstacles_in_buffer.map(o => [
            o.name,
            o.distance_nm,
            (o.distance_m / 1000).toFixed(2),
            o.elevation_m,
            o.v_tolerance,
            o.total_elev_ft.toFixed(0),
            o.area === 'primary' ? 'Primary' : 'Secondary',
            o.moc_applied_ft.toFixed(0),
            o.obstacle_moca_ft.toFixed(0),
            o.segment_from && o.segment_to ? `${o.segment_from}-${o.segment_to}` : '—',
            o.has_published_moca ? o.segment_moca_ft.toFixed(0) : 'Not published',
            o.segment_mea_fl != null ? o.segment_mea_fl : '—',
            !o.has_published_moca ? 'NO PUBLISHED MOCA' : (o.penetrates ? 'PENETRATES' : 'CLEAR'),
        ]);

        const csvContent = [
            headers.join(","),
            ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `RNAV_Route_${activeRoute.route_name.replace(/[\/\\]/g, '_')}_Assessment.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderResultsContent = () => {
        if (!activeRoute) return null;
        return (
            <div style={{ overflowY: 'auto', flex: 1, padding: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, textAlign: 'left' }}>
                    <thead>
                        <tr style={{ background: T.bg0, borderBottom: `2px solid ${T.border}` }}>
                            <th style={{ padding: '6px 8px', color: T.muted }}>Obstacle Name</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Distance</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Elevation (m)</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>V-Accuracy (m)</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Total Elev (ft)</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Area</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>MOC Applied (ft)</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Obstacle MOCA (ft)</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Segment</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>Published MOCA (ft)</th>
                            <th style={{ padding: '6px 8px', color: T.muted, textAlign: 'center' }}>MEA (FL)</th>
                            <th style={{ padding: '6px 8px', color: T.muted }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeRoute.obstacles_in_buffer.map((obs, idx) => {
                            const isControlling = obs.is_controlling;
                            const statusBadge = !obs.has_published_moca
                                ? { bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)', color: T.muted, label: 'NO PUBLISHED MOCA' }
                                : obs.penetrates
                                ? { bg: 'rgba(255,95,95,0.12)', border: 'rgba(255,95,95,0.3)', color: '#ff5f5f', label: 'PENETRATES' }
                                : { bg: 'rgba(46,201,139,0.12)', border: 'rgba(46,201,139,0.3)', color: '#2ec98b', label: 'CLEAR' };
                            return (
                                <tr key={idx} style={{
                                    borderBottom: `1px solid ${T.border}15`,
                                    background: isControlling ? 'rgba(0, 225, 255, 0.04)' : idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'
                                }}>
                                    <td style={{ padding: '6px 8px', color: T.white, fontWeight: isControlling ? 700 : 400 }}>
                                        {obs.name} {isControlling && <span style={{ color: '#00e1ff', fontSize: 9 }}>[Controlling]</span>}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>
                                        {obs.distance_nm.toFixed(2)} NM ({Math.round(obs.distance_m)} m)
                                    </td>
                                    <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>{obs.elevation_m.toFixed(1)}</td>
                                    <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>{obs.v_tolerance.toFixed(1)}</td>
                                    <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>{obs.total_elev_ft.toFixed(0)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                        <span style={{
                                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                                            background: obs.area === 'primary' ? 'rgba(34,102,238,0.15)' : 'rgba(255,153,0,0.15)',
                                            border: `1px solid ${obs.area === 'primary' ? 'rgba(34,102,238,0.4)' : 'rgba(255,153,0,0.4)'}`,
                                            color: obs.area === 'primary' ? '#5b8fff' : '#ffb84d',
                                        }}>
                                            {obs.area === 'primary' ? 'PRIMARY' : 'SECONDARY'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>{obs.moc_applied_ft.toFixed(0)}</td>
                                    <td style={{ padding: '6px 8px', color: isControlling ? '#00e1ff' : T.white, fontWeight: isControlling ? 700 : 400, textAlign: 'center' }}>
                                        {obs.obstacle_moca_ft.toFixed(0)}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: T.muted, textAlign: 'center', fontSize: 10 }}>
                                        {obs.segment_from && obs.segment_to ? `${obs.segment_from}-${obs.segment_to}` : '—'}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: obs.has_published_moca ? '#00e1ff' : T.muted, fontWeight: obs.has_published_moca ? 700 : 400, textAlign: 'center' }}>
                                        {obs.has_published_moca ? Math.round(obs.segment_moca_ft) : '—'}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: T.white, textAlign: 'center' }}>
                                        {obs.segment_mea_fl != null ? `FL${Math.round(obs.segment_mea_fl)}` : '—'}
                                    </td>
                                    <td style={{ padding: '6px 8px' }}>
                                        <span style={{ fontSize: 9, background: statusBadge.bg, border: `1px solid ${statusBadge.border}`, color: statusBadge.color, padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>
                                            {statusBadge.label}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=rnav`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_RNAV_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    return (

        <div style={{ display: 'flex', flex: 1, height: '100%', minHeight: 0, background: T.bg2, position: 'relative' }}>
            {/* Sidebar (List of routes and active route stats) */}
            <div style={{
                width: 320, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column',
                background: T.bg1, zIndex: 10, flexShrink: 0
            }}>
                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Enroute RNAV Routes
                    </div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                        Routes touching or inside a 5.77 NM obstacle buffer
                    </div>
                    <button
                        onClick={handleExportAixm}
                        disabled={exportingAixm}
                        style={{ marginTop: 10, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                        title="Export obstacles, aerodrome data, waypoints and RNAV ATS routes (Route/RouteSegment) as AIXM 5.1"
                    >
                        {exportingAixm ? '⏳…' : '📤 AIXM'}
                    </button>
                </div>

                {loading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: T.muted, fontSize: 12 }}>
                        Loading routes...
                    </div>
                ) : error ? (
                    <div style={{ padding: 16, color: T.danger, fontSize: 11 }}>
                        Error: {error}
                    </div>
                ) : assessResults.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: T.muted, fontSize: 12 }}>
                        No routes fall within 5.77 NM of any project obstacles.
                    </div>
                ) : (
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {assessResults.map(r => {
                            const penetratingCount = r.obstacles_in_buffer.filter(o => o.penetrates).length;
                            return (
                                <div
                                    key={r.route_id}
                                    onClick={() => setActiveRouteId(r.route_id)}
                                    style={{
                                        padding: 12, borderRadius: 8, cursor: 'pointer',
                                        border: activeRouteId === r.route_id ? '1px solid var(--blue-glass-border)' : '1px solid transparent',
                                        background: activeRouteId === r.route_id ? 'var(--blue-glass-fill)' : 'transparent',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 600, color: activeRouteId === r.route_id ? T.blueLt : T.white, fontSize: 13 }}>
                                            {r.route_name}
                                        </span>
                                        <span style={{
                                            fontSize: 10, padding: '1px 5px', borderRadius: 4,
                                            background: penetratingCount > 0 ? 'rgba(255, 95, 95, 0.12)' : 'rgba(255,255,255,0.06)',
                                            border: `1px solid ${penetratingCount > 0 ? 'rgba(255, 95, 95, 0.25)' : T.border}`,
                                            color: penetratingCount > 0 ? '#ff5f5f' : T.muted,
                                        }}>
                                            {penetratingCount > 0 ? `${penetratingCount} PENETRATING` : `${r.obstacles_in_buffer.length} in buffer`}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 11, color: T.muted, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <span>Controlling obstacle MOCA: <strong style={{ color: T.white }}>
                                            {r.controlling_obstacle_moca_ft != null ? `${r.controlling_obstacle_moca_ft.toFixed(0)} ft` : '—'}
                                        </strong> ({r.controlling_obstacle_name || '—'})</span>
                                        <span>MEA: <strong style={{ color: T.white }}>FL200</strong></span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* General Action Buttons */}
                <div style={{ padding: 12, borderTop: `1px solid ${T.border}33`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button
                        onClick={handleCapture}
                        disabled={capturing || assessResults.length === 0}
                        style={{
                            width: '100%', padding: '8px 12px', border: 'none', borderRadius: 6,
                            background: capturing ? T.muted : T.blue, color: T.white, fontSize: 11,
                            fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                        }}
                    >
                        📸 {capturing ? 'Capturing Map...' : 'Capture Map Screenshot'}
                    </button>
                    {hasScreenshot && (
                        <div style={{ fontSize: 10, color: T.safe, textAlign: 'center' }}>
                            ✓ Saved map screenshot will render in Report.
                        </div>
                    )}
                </div>
            </div>

            {/* Map & Results Panel */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {/* Map Area */}
                <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                    <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportShieldCsv}
                        penetratingCount={rnavShieldRealPenetration.size}
                        onExportPenetrating={() => {
                            const byId = new Map();
                            [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                             ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                                byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                            });
                            const entries = [];
                            rnavShieldRealPenetration.forEach((penList, obsId) => {
                                const r = byId.get(obsId);
                                if (!r) return;
                                penList.forEach(v => entries.push({
                                    r,
                                    surfaceLabel: `RNAV ${v.route_name} ${v.segment_from}-${v.segment_to}`,
                                    penetrationAmount: (-v.margin).toFixed(2),
                                }));
                            });
                            exportSurfacePenetratingCsv(entries, radiusKm);
                        }} />
                    <MapMeasureTool map={mapState} top={52} tableOffset={activeRoute ? (resultsCollapsed ? 40 : 300) : 0} />
                    <MapCoordinateViewer map={mapState} />
                    <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />
                    
                    {/* Layer Mode Swapper */}
                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 100, display: 'flex', gap: 4, background: T.bg1, padding: 4, borderRadius: 8, border: `1px solid ${T.border}` }}>
                        {Object.entries(TILE_LAYERS).map(([k, l]) => (
                            <button
                                key={k}
                                onClick={() => setMapMode(k)}
                                style={{
                                    padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, cursor: 'pointer',
                                    background: mapMode === k ? 'rgba(58, 138, 196, 0.15)' : 'transparent',
                                    color: mapMode === k ? T.blueLt : T.muted, fontWeight: mapMode === k ? 700 : 400
                                }}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Collapsible bottom drawer for Obstacles */}
                {activeRoute && (
                    <div style={{
                        background: T.bg1, borderTop: `1px solid ${T.border}`,
                        display: 'flex', flexDirection: 'column',
                        height: resultsCollapsed ? 40 : resultsHeight,
                        transition: 'height 0.25s ease-in-out',
                        overflow: 'hidden'
                    }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        {/* Drawer Header */}
                        <div style={{
                            padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            cursor: 'pointer', borderBottom: `1px solid ${T.border}22`, background: T.bg2
                        }} onClick={() => setResultsCollapsed(!resultsCollapsed)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.blueLt }}>
                                    Route {activeRoute.route_name} Buffer Obstacles List
                                </span>
                                <span style={{ fontSize: 11, background: T.border, color: T.white, padding: '1px 6px', borderRadius: 10 }}>
                                    {activeRoute.obstacles_in_buffer.length}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={(e) => { e.stopPropagation(); exportCsv(); }}
                                    style={{
                                        padding: '2px 8px', borderRadius: 4, border: 'none', fontSize: 11, cursor: 'pointer',
                                        background: T.blue, color: T.white, fontWeight: 700
                                    }}
                                >
                                    📥 Export CSV
                                </button>
                                <span style={{ color: T.muted, fontSize: 12 }}>{resultsCollapsed ? '▲' : '▼'}</span>
                            </div>
                        </div>

                        {/* Drawer Body (Table of obstacles) */}
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out.
                                    <button
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }}
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : renderResultsContent()}
                        </div>
                    </div>
                )}
            </div>

            {resultsPoppedOut && activeRoute && (
                <DraggableModal
                    title={`${project?.name || ''} - Route ${activeRoute.route_name} Buffer Obstacles`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>

    );
}
